/**
 * Demand lookup (R17, ADR-035).
 *
 * BLS projects at the 6-digit SOC level (11-3021); O*NET resolves people to
 * 8-digit detail codes (11-3021.00, 15-1299.09). One BLS row therefore serves
 * every detail code beneath it, which is the correct relationship rather than a
 * convenience — the projection is genuinely made at the SOC level, and pretending
 * otherwise would imply a precision the source does not have.
 *
 * Everything here degrades to null rather than throwing. Demand is an enrichment;
 * if the import has not been run, the product carries on without it and the UI
 * simply omits the line.
 */
let cache = null;

/**
 * `demand.json` is COMMITTED as an empty placeholder, and the importer
 * overwrites it. That is not tidiness — it is required.
 *
 * Rollup resolves dynamic imports at BUILD time, so wrapping this in try/catch
 * protects nothing: a missing file fails the whole bundle. The mistake is easy
 * to make because Node resolves lazily, so the same code passes a script test
 * and then breaks `vite build`.
 *
 * An empty `bySoc` is the "not imported yet" state, and every caller already
 * treats a missing row as null.
 */
export async function loadDemand() {
  if (cache !== null) return cache;
  const mod = await import("../data/taxonomy/demand.json");
  cache = mod.default || { bySoc: {} };
  return cache;
}

export const socOf = (code) => String(code || "").slice(0, 7);

export function demandFor(demand, code) {
  const row = demand?.bySoc?.[socOf(code)];
  if (!row) return null;
  return {
    ...row,
    // A ten-year projection divided by nothing is still a projection. The label
    // travels with the number so no screen can accidentally present it as fact.
    label: "projected annual openings",
  };
}

/**
 * The reading that matters: demand alone is not opportunity.
 *
 * A role with plenty of openings and high exposure is where the market is still
 * hiring for work that is being automated underneath — the single most expensive
 * mistake someone can make with this data, and the reason the two numbers belong
 * on the same card rather than on separate screens.
 */
export function demandVerdict(row, exposure) {
  if (!row || typeof exposure !== "number") return null;
  const openings = row.annualOpenings;
  const growth = row.growthPct;
  if (openings == null && growth == null) return null;

  const shrinking = typeof growth === "number" && growth < 0;
  const exposed = exposure >= 6;
  const durable = exposure < 4;

  if (exposed && !shrinking && openings) {
    return {
      tone: "warn",
      text: "Still hiring, and heavily exposed. Openings today are not the same as a job in five years.",
    };
  }
  if (shrinking && exposed) {
    return { tone: "warn", text: "Shrinking and exposed. The clearest signal on this screen." };
  }
  if (durable && typeof growth === "number" && growth > 0) {
    return { tone: "good", text: "Growing, and the work resists automation. The rare combination." };
  }
  if (shrinking) {
    return { tone: "warn", text: "Projected to shrink, though the work itself holds up." };
  }
  return null;
}

/** 1,847,900 → "1.8M" · 93,300 → "93K" */
export const fmtCount = (n) =>
  typeof n !== "number" ? "—"
  : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M`
  : n >= 1e3 ? `${Math.round(n / 1e3)}K`
  : String(n);

export const fmtGrowth = (n) =>
  typeof n !== "number" ? null : `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;
